import React, { useState, useEffect } from 'react'
import { api, formatCurrency, formatDate } from '../../utils/api'
import DealDetailModal from './DealDetailModal'

const CARRIER_COLORS = {
  'royal mail': '#E74C3C',
  'royal_mail': '#E74C3C',
  'parcelforce': '#3498DB',
  'fedex': '#4D148C',
  'shiply': '#F1C40F',
  'dhl': '#FFCC00'
}

function getCarrierColor(carrier) {
  const c = (carrier || '').toLowerCase()
  for (const [key, color] of Object.entries(CARRIER_COLORS)) {
    if (c.includes(key)) return color
  }
  return '#666'
}

const STATUS_BADGES = {
  auto_exception: { label: 'Auto Exception', bg: '#fef2f2', color: '#dc2626' },
  no_tracking: { label: 'No Tracking', bg: '#fef2f2', color: '#dc2626' },
  not_sent: { label: 'Not Sent', bg: '#fef2f2', color: '#dc2626' },
  delivered: { label: 'Delivered', bg: '#f0fdf4', color: '#16a34a' },
  in_transit: { label: 'In Transit', bg: '#fffbeb', color: '#d97706' },
  manual_check: { label: 'Manual Check', bg: '#fff7ed', color: '#ea580c' },
  unknown: { label: 'Unknown', bg: '#f3f4f6', color: '#6b7280' }
}

function StatusBadge({ status, text }) {
  const badge = STATUS_BADGES[status] || STATUS_BADGES.unknown
  return (
    <div>
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        background: badge.bg,
        color: badge.color
      }}>
        {badge.label}
      </span>
      {text && (
        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2, maxWidth: 180 }}>
          {text}
        </div>
      )}
    </div>
  )
}

const TABS = [
  { key: 'unshipped', label: 'Unshipped', color: '#dc2626' },
  { key: 'manualCheck', label: 'Manual Check', color: '#ea580c' },
  { key: 'noTracking', label: 'No Tracking', color: '#9333ea' },
  { key: 'arrived', label: 'Arrived', color: '#16a34a' },
  { key: 'inTransit', label: 'In Transit', color: '#3b82f6' }
]

const CHASE_TABS = ['unshipped', 'manualCheck']
const CHASE_COOLDOWN_MS = 24 * 60 * 60 * 1000

function chaseCooldown(lastChasedAt) {
  if (!lastChasedAt) return null
  const elapsed = Date.now() - new Date(lastChasedAt + 'Z').getTime()
  if (elapsed >= CHASE_COOLDOWN_MS) return null
  const hoursLeft = Math.ceil((CHASE_COOLDOWN_MS - elapsed) / (60 * 60 * 1000))
  return hoursLeft
}

function timeAgo(dateStr) {
  if (!dateStr) return null
  const elapsed = Date.now() - new Date(dateStr + 'Z').getTime()
  const hours = Math.floor(elapsed / (60 * 60 * 1000))
  if (hours < 1) return 'just now'
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function CollectionExceptions() {
  const [data, setData] = useState({ unshipped: [], manualCheck: [], noTracking: [], arrived: [], inTransit: [], stats: {} })
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('unshipped')
  const [movingDeal, setMovingDeal] = useState(null)
  const [selectedDeal, setSelectedDeal] = useState(null)
  const [checkedDeals, setCheckedDeals] = useState(new Set())
  const [chasingDeal, setChasingDeal] = useState(null)
  const [chasingBulk, setChasingBulk] = useState(false)
  const [chaseResults, setChaseResults] = useState(null)
  useEffect(() => {
    loadData()
  }, [])

  async function loadData(showSpinner = true) {
    if (showSpinner) setLoading(true)
    try {
      const response = await api.get('/api/deals/collection-exceptions')
      setData({
        unshipped: response.unshipped || [],
        manualCheck: response.manualCheck || [],
        noTracking: response.noTracking || [],
        arrived: response.arrived || [],
        inTransit: response.inTransit || [],
        stats: response.stats || {}
      })
    } catch (error) {
      console.error('Failed to load collection exceptions:', error)
    } finally {
      setLoading(false)
    }
  }

  function removeDealFromList(dealId) {
    setData(prev => {
      const updated = { ...prev }
      for (const key of ['unshipped', 'manualCheck', 'noTracking', 'arrived', 'inTransit']) {
        updated[key] = prev[key].filter(d => d.id !== dealId)
      }
      updated.stats = { ...prev.stats, total: Math.max(0, (prev.stats.total || 1) - 1) }
      if (prev.stats[activeTab]) {
        updated.stats[activeTab] = Math.max(0, prev.stats[activeTab] - 1)
      }
      return updated
    })
  }

  async function moveToArrived(dealId) {
    setMovingDeal(dealId)
    try {
      await api.post('/api/deals/collection-exceptions/move-to-arrived', { dealId })
      setData(prev => ({
        ...prev,
        arrived: prev.arrived.filter(d => d.id !== dealId),
        stats: { ...prev.stats, arrived: Math.max(0, (prev.stats.arrived || 1) - 1) }
      }))
    } catch (error) {
      console.error('Move to arrived failed:', error)
      alert('Failed to move deal: ' + error.message)
    } finally {
      setMovingDeal(null)
    }
  }

  const showChase = CHASE_TABS.includes(activeTab)

  function toggleDeal(dealId) {
    setCheckedDeals(prev => {
      const next = new Set(prev)
      if (next.has(dealId)) next.delete(dealId)
      else next.add(dealId)
      return next
    })
  }

  function toggleAll() {
    const eligible = deals.filter(d => !chaseCooldown(d.last_chased_at))
    if (checkedDeals.size === eligible.length && eligible.length > 0) {
      setCheckedDeals(new Set())
    } else {
      setCheckedDeals(new Set(eligible.map(d => d.id)))
    }
  }

  async function sendChaseEmail(deal) {
    setChasingDeal(deal.id)
    setChaseResults(null)
    try {
      const response = await api.post('/api/deals/collection-exceptions/send-chase', {
        deals: [{ dealId: deal.id, dealname: deal.dealname, sendType: deal.send_type, carrier: deal.carrier, trackingNumber: deal.tracking_number }]
      })
      const r = response.results?.[0]
      if (r?.success) {
        setChaseResults({ type: 'success', message: `Chase email sent to ${r.email}` })
      } else {
        setChaseResults({ type: 'error', message: r?.error || 'Failed to send' })
      }
    } catch (error) {
      setChaseResults({ type: 'error', message: error.message })
    } finally {
      setChasingDeal(null)
      setTimeout(() => setChaseResults(null), 5000)
    }
  }

  async function sendBulkChase() {
    if (checkedDeals.size === 0) return
    setChasingBulk(true)
    setChaseResults(null)
    try {
      const dealsToChase = deals
        .filter(d => checkedDeals.has(d.id))
        .map(d => ({ dealId: d.id, dealname: d.dealname, sendType: d.send_type, carrier: d.carrier, trackingNumber: d.tracking_number }))
      const response = await api.post('/api/deals/collection-exceptions/send-chase', { deals: dealsToChase })
      setChaseResults({
        type: response.failed > 0 ? 'warning' : 'success',
        message: `${response.sent} sent, ${response.failed} failed`,
        details: response.results
      })
      setCheckedDeals(new Set())
    } catch (error) {
      setChaseResults({ type: 'error', message: error.message })
    } finally {
      setChasingBulk(false)
      setTimeout(() => setChaseResults(null), 8000)
    }
  }

  const deals = data[activeTab] || []

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div className="loading-spinner" />
        <p style={{ color: 'var(--muted)', marginTop: 12 }}>Loading deals and checking tracking...</p>
      </div>
    )
  }

  return (
    <div style={{ marginTop: 20 }}>
      {/* Stats Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 12,
        marginBottom: 20
      }}>
        <StatCard label="Total Shipping" value={data.stats.total || 0} color="var(--color-info)" />
        <StatCard label="Unshipped" value={data.stats.unshipped || 0} color="#dc2626" />
        <StatCard label="Manual Check" value={data.stats.manualCheck || 0} color="#ea580c" />
        <StatCard label="No Tracking" value={data.stats.noTracking || 0} color="#9333ea" />
        <StatCard label="Arrived" value={data.stats.arrived || 0} color="#16a34a" />
        <StatCard label="In Transit" value={data.stats.inTransit || 0} color="#3b82f6" />
      </div>

      {/* Tab Bar + Refresh */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 0 }}>
          {TABS.map((tab, i) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setCheckedDeals(new Set()); setChaseResults(null) }}
              style={{
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: activeTab === tab.key ? 700 : 500,
                border: '1px solid var(--border)',
                borderRight: i < TABS.length - 1 ? 'none' : '1px solid var(--border)',
                borderRadius: i === 0 ? '6px 0 0 6px' : i === TABS.length - 1 ? '0 6px 6px 0' : 0,
                background: activeTab === tab.key ? tab.color : 'var(--bg-secondary)',
                color: activeTab === tab.key ? 'white' : 'var(--text)',
                cursor: 'pointer'
              }}
            >
              {tab.label} ({data[tab.key]?.length || 0})
            </button>
          ))}
        </div>
        <button className="btn btn-sm btn-secondary" onClick={loadData} disabled={loading}>
          Refresh
        </button>
      </div>

      {/* Chase Results Toast */}
      {chaseResults && (
        <div style={{
          padding: '10px 16px',
          marginBottom: 12,
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 500,
          background: chaseResults.type === 'success' ? '#f0fdf4' : chaseResults.type === 'warning' ? '#fffbeb' : '#fef2f2',
          color: chaseResults.type === 'success' ? '#16a34a' : chaseResults.type === 'warning' ? '#d97706' : '#dc2626',
          border: `1px solid ${chaseResults.type === 'success' ? '#bbf7d0' : chaseResults.type === 'warning' ? '#fde68a' : '#fecaca'}`
        }}>
          {chaseResults.message}
          {chaseResults.details && chaseResults.details.some(r => !r.success) && (
            <div style={{ marginTop: 6, fontSize: 11 }}>
              {chaseResults.details.filter(r => !r.success).map((r, i) => (
                <div key={i}>{r.dealname || r.dealId}: {r.error}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bulk Action Bar */}
      {showChase && checkedDeals.size > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 16px',
          marginBottom: 12,
          borderRadius: 6,
          background: '#eff6ff',
          border: '1px solid #bfdbfe'
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8' }}>
            {checkedDeals.size} deal{checkedDeals.size > 1 ? 's' : ''} selected
          </span>
          <button
            className="btn btn-sm"
            onClick={sendBulkChase}
            disabled={chasingBulk}
            style={{ background: '#d97706', color: 'white', border: 'none', fontSize: 12 }}
          >
            {chasingBulk ? 'Sending...' : `Send Chase Email${checkedDeals.size > 1 ? 's' : ''}`}
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => setCheckedDeals(new Set())}
            style={{ fontSize: 12 }}
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      {deals.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>
          {activeTab === 'unshipped' && 'No unshipped deals. All tracked items appear to have been sent.'}
          {activeTab === 'manualCheck' && 'No deals requiring manual verification.'}
          {activeTab === 'noTracking' && 'No deals without tracking numbers.'}
          {activeTab === 'arrived' && 'No delivered deals awaiting processing.'}
          {activeTab === 'inTransit' && 'No items currently in transit.'}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="table" style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                {showChase && (
                  <th style={{ width: 36, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={deals.length > 0 && checkedDeals.size === deals.length}
                      onChange={toggleAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                )}
                <th style={{ width: '22%' }}>Deal</th>
                <th>Type</th>
                <th>Carrier</th>
                <th>Tracking</th>
                <th>Collection Date</th>
                {activeTab === 'unshipped' && <th>Overdue</th>}
                <th>Status</th>
                {(activeTab === 'arrived' || activeTab === 'inTransit') && <th style={{ textAlign: 'right' }}>Amount</th>}
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deals.map(deal => (
                <tr key={deal.id} className="hover-row">
                  {showChase && (
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={checkedDeals.has(deal.id)}
                        onChange={() => toggleDeal(deal.id)}
                        disabled={!!chaseCooldown(deal.last_chased_at)}
                        style={{ cursor: chaseCooldown(deal.last_chased_at) ? 'not-allowed' : 'pointer', opacity: chaseCooldown(deal.last_chased_at) ? 0.4 : 1 }}
                      />
                    </td>
                  )}
                  <td>
                    <div
                      style={{ fontWeight: 600, lineHeight: 1.3, cursor: 'pointer', color: 'var(--primary)' }}
                      onClick={() => setSelectedDeal(deal)}
                    >
                      {deal.dealname || `Deal ${deal.id}`}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>ID: {deal.id}</div>
                  </td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'white',
                      background: deal.send_type === 'collection' ? '#3b82f6' : deal.send_type === 'dropoff' ? '#8b5cf6' : '#9ca3af'
                    }}>
                      {deal.send_type === 'collection' ? 'Collection' : deal.send_type === 'dropoff' ? 'Drop Off' : 'Not Set'}
                    </span>
                  </td>
                  <td>
                    {deal.carrier ? (
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'white',
                        background: getCarrierColor(deal.carrier)
                      }}>
                        {deal.carrier}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--muted)', fontSize: 12 }}>None</span>
                    )}
                  </td>
                  <td>
                    {deal.trackingUrls && deal.trackingUrls.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {deal.trackingUrls.map((t, i) => (
                          <a
                            key={i}
                            href={t.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--primary)' }}
                            title={t.number}
                          >
                            {t.number.length > 20 ? t.number.substring(0, 20) + '...' : t.number}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#dc2626', fontSize: 12, fontWeight: 500 }}>No tracking</span>
                    )}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {deal.collection_date ? formatDate(deal.collection_date) : (
                      <span style={{ color: 'var(--muted)' }}>None</span>
                    )}
                  </td>
                  {activeTab === 'unshipped' && (
                    <td>
                      {deal.daysOverdue > 0 ? (
                        <span style={{
                          fontWeight: 700,
                          color: deal.daysOverdue > 5 ? '#dc2626' : deal.daysOverdue > 2 ? '#d97706' : '#6b7280'
                        }}>
                          {deal.daysOverdue}d
                        </span>
                      ) : (
                        <span style={{ color: 'var(--muted)' }}>—</span>
                      )}
                    </td>
                  )}
                  <td>
                    <StatusBadge status={deal.trackingStatus} text={deal.trackingStatusText} />
                  </td>
                  {(activeTab === 'arrived' || activeTab === 'inTransit') && (
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {deal.amount ? formatCurrency(deal.amount) : '—'}
                    </td>
                  )}
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                      {showChase && (() => {
                        const cooldown = chaseCooldown(deal.last_chased_at)
                        const chased = timeAgo(deal.last_chased_at)
                        return cooldown ? (
                          <span style={{ fontSize: 10, color: 'var(--muted)' }} title={`Chased ${chased} — wait ${cooldown}h`}>
                            Chased {chased}
                          </span>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {chased && <span style={{ fontSize: 10, color: 'var(--muted)' }}>Chased {chased}</span>}
                            <button
                              className="btn btn-xs"
                              onClick={() => sendChaseEmail(deal)}
                              disabled={chasingDeal === deal.id}
                              style={{ fontSize: 11, background: '#d97706', color: 'white', border: 'none' }}
                            >
                              {chasingDeal === deal.id ? 'Sending...' : 'Chase'}
                            </button>
                          </div>
                        )
                      })()}
                      {activeTab === 'arrived' && (
                        <button
                          className="btn btn-xs btn-success"
                          onClick={() => moveToArrived(deal.id)}
                          disabled={movingDeal === deal.id}
                          style={{ fontSize: 11 }}
                        >
                          {movingDeal === deal.id ? 'Moving...' : 'Move to Arrived'}
                        </button>
                      )}
                      {deal.trackingUrls?.[0] && (
                        <a
                          href={deal.trackingUrls[0].url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-xs btn-secondary"
                          style={{ fontSize: 11, textDecoration: 'none' }}
                        >
                          Track
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selectedDeal && (
        <DealDetailModal
          deal={selectedDeal}
          onClose={() => setSelectedDeal(null)}
          onDealUpdated={() => { const id = selectedDeal.id; setSelectedDeal(null); removeDealFromList(id); loadData(false) }}
        />
      )}
    </div>
  )
}

function StatCard({ label, value, color }) {
  return (
    <div className="card" style={{ padding: '12px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{label}</div>
    </div>
  )
}

export default CollectionExceptions
